import React, { useMemo } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { HourglassTop, PersonAdd, WarningAmber } from '@mui/icons-material'
import moment from 'moment'

const getWaitMinutes = (arrivedAt) => {
  if (!arrivedAt) return 0
  return moment().diff(moment(arrivedAt), 'minutes')
}

const priorityColor = (priority) => {
  if (priority === 'Critical') return 'error'
  if (priority === 'High') return 'warning'
  if (priority === 'Moderate') return 'info'
  return 'default'
}

const WaitingRoomCard = ({
  patients = [],
  onPatientClick,
  onAddPatient,
  canAddPatient = true,
}) => {
  const sortedPatients = useMemo(() => {
    return [...patients].sort(
      (a, b) => new Date(a.arrivedAt) - new Date(b.arrivedAt),
    )
  }, [patients])

  const waitTimes = sortedPatients.map((p) => getWaitMinutes(p.arrivedAt))

  const longestWait = waitTimes.length ? Math.max(...waitTimes) : 0

  const averageWait = waitTimes.length
    ? Math.round(
        waitTimes.reduce((sum, value) => sum + value, 0) / waitTimes.length,
      )
    : 0

  const pressure =
    longestWait >= 45 ? 'critical' : longestWait >= 25 ? 'warning' : 'normal'

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        border:
          pressure === 'critical'
            ? '1px solid #FCA5A5'
            : pressure === 'warning'
              ? '1px solid #FCD34D'
              : '1px solid rgba(15,23,42,0.08)',
        background:
          pressure === 'critical'
            ? 'linear-gradient(135deg, #FFFFFF 0%, #FEF2F2 100%)'
            : pressure === 'warning'
              ? 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)'
              : '#FFFFFF',
        boxShadow: '0 12px 32px rgba(15,23,42,0.08)',
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 3,
                backgroundColor: '#F5F3FF',
                color: '#7C3AED',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HourglassTop />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={900} color="#0F172A">
                Waiting Room
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Patients awaiting assessment or allocation
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAdd />}
            onClick={onAddPatient}
            disabled={!canAddPatient}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#7C3AED',
              // Keep it a pill: don't let the row stretch it into a
              // circle when the caption wraps on narrow screens.
              alignSelf: 'flex-start',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              px: 2,
            }}
          >
            {canAddPatient ? 'Add' : 'Full'}
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={`${patients.length} waiting`}
            size="small"
            sx={{
              borderRadius: 999,
              fontWeight: 900,
              backgroundColor: '#F5F3FF',
              color: '#6D28D9',
            }}
          />

          <Chip
            label={`Longest ${longestWait}m`}
            size="small"
            icon={pressure !== 'normal' ? <WarningAmber /> : undefined}
            color={
              pressure === 'critical'
                ? 'error'
                : pressure === 'warning'
                  ? 'warning'
                  : 'default'
            }
            sx={{
              borderRadius: 999,
              fontWeight: 900,
            }}
          />

          <Chip
            label={`Avg ${averageWait}m`}
            size="small"
            sx={{
              borderRadius: 999,
              fontWeight: 900,
              backgroundColor: '#F8FAFC',
            }}
          />
        </Stack>

        <Divider />

        {sortedPatients.length === 0 ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              backgroundColor: '#F8FAFC',
              border: '1px dashed rgba(15,23,42,0.16)',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No patients currently waiting.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1}>
            {sortedPatients.map((patient) => {
              const wait = getWaitMinutes(patient.arrivedAt)

              return (
                <Paper
                  key={patient.id}
                  elevation={0}
                  onClick={() => onPatientClick?.(patient)}
                  sx={{
                    p: 1.25,
                    borderRadius: 3,
                    border: '1px solid rgba(15,23,42,0.08)',
                    cursor: 'pointer',
                    backgroundColor: wait >= 45 ? '#FEF2F2' : '#F8FAFC',
                    '&:hover': {
                      backgroundColor: '#EEF2F7',
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography variant="body2" fontWeight={900}>
                        {patient.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {patient.incidentNumber} • {patient.complaint}
                      </Typography>
                    </Box>

                    <Stack alignItems="flex-end" spacing={0.5}>
                      <Chip
                        size="small"
                        label={patient.priority}
                        color={priorityColor(patient.priority)}
                        sx={{ borderRadius: 999, fontWeight: 800 }}
                      />

                      <Typography
                        variant="caption"
                        fontWeight={900}
                        color={wait >= 45 ? 'error.main' : 'text.secondary'}
                      >
                        {wait}m wait
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              )
            })}
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

export default WaitingRoomCard
